import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { isTransactionsTransitionInvalidTransition, storableError } from '../../util/errors';
import {
  transitionPrivileged,
  relistOrderItems,
  createLabelPaymentIntent,
  submitManualTracking as submitManualTrackingApi,
  fetchOpenReturnRefund,
  uploadOrderPhoto as uploadOrderPhotoApi,
  BackgroundServiceApi,
} from '../../util/api';
import {
  isPrivileged as isPrivilegedPurchaseTransition,
  transitions as T,
} from '../../transactions/transactionProcessPurchase';
import { resetNotificationAuto, trackNotificationAuto } from '../../util/notificationHelpers';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import {
  PURCHASE_PROCESS_NAME,
  getProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser, fetchCurrentUserNotifications } from '../../ducks/user.duck';
import { clearLabelBoughtForOtherOrder } from '../../ducks/shipping.duck';
import { checkAddressWithCarrier } from '../EditListingPage/EditListingPage.duck';
import { CUSTOMER_RETURN_TRANSITION_BY_STATE } from './TransactionPage.stateDataPurchase';
import { PASSING_STATES, RELIST } from './orderTransitions';
import { delay } from '../../util/sdkRetry';

const { UUID } = sdkTypes;

const MESSAGES_PAGE_SIZE = 100;
const REVIEW_TX_INCLUDES = ['reviews', 'reviews.author', 'reviews.subject'];

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/TransactionPage/SET_INITIAL_VALUES';

export const FETCH_TRANSACTION_REQUEST = 'app/TransactionPage/FETCH_TRANSACTION_REQUEST';
export const FETCH_TRANSACTION_SUCCESS = 'app/TransactionPage/FETCH_TRANSACTION_SUCCESS';
export const FETCH_TRANSACTION_ERROR = 'app/TransactionPage/FETCH_TRANSACTION_ERROR';

export const FETCH_TRANSITIONS_SUCCESS = 'app/TransactionPage/FETCH_TRANSITIONS_SUCCESS';

export const TRANSITION_REQUEST = 'app/TransactionPage/MARK_RECEIVED_REQUEST';
export const TRANSITION_SUCCESS = 'app/TransactionPage/TRANSITION_SUCCESS';
export const TRANSITION_ERROR = 'app/TransactionPage/TRANSITION_ERROR';

export const FETCH_MESSAGES_REQUEST = 'app/TransactionPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/TransactionPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/TransactionPage/FETCH_MESSAGES_ERROR';

export const SEND_MESSAGE_REQUEST = 'app/TransactionPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/TransactionPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/TransactionPage/SEND_MESSAGE_ERROR';

export const SEND_REVIEW_REQUEST = 'app/TransactionPage/SEND_REVIEW_REQUEST';
export const SEND_REVIEW_SUCCESS = 'app/TransactionPage/SEND_REVIEW_SUCCESS';
export const SEND_REVIEW_ERROR = 'app/TransactionPage/SEND_REVIEW_ERROR';
export const CLEAR_SEND_REVIEW_ERROR = 'app/TransactionPage/CLEAR_SEND_REVIEW_ERROR';

export const VALIDATE_ADDRESS_REQUEST = 'app/TransactionPage/VALIDATE_ADDRESS_REQUEST';
export const VALIDATE_ADDRESS_SUCCESS = 'app/TransactionPage/VALIDATE_ADDRESS_SUCCESS';
export const VALIDATE_ADDRESS_ERROR = 'app/TransactionPage/VALIDATE_ADDRESS_ERROR';
export const CLEAR_ADDRESS_VALIDATION = 'app/TransactionPage/CLEAR_ADDRESS_VALIDATION';

export const SUBMIT_MANUAL_TRACKING_REQUEST = 'app/TransactionPage/SUBMIT_MANUAL_TRACKING_REQUEST';
export const SUBMIT_MANUAL_TRACKING_SUCCESS = 'app/TransactionPage/SUBMIT_MANUAL_TRACKING_SUCCESS';
export const SUBMIT_MANUAL_TRACKING_ERROR = 'app/TransactionPage/SUBMIT_MANUAL_TRACKING_ERROR';

export const CALCULATE_RETURN_REQUEST = 'app/TransactionPage/CALCULATE_RETURN_REQUEST';
export const CALCULATE_RETURN_SUCCESS = 'app/TransactionPage/CALCULATE_RETURN_SUCCESS';
export const CALCULATE_RETURN_ERROR = 'app/TransactionPage/CALCULATE_RETURN_ERROR';

export const QUOTE_RETURN_LABEL_REQUEST = 'app/TransactionPage/QUOTE_RETURN_LABEL_REQUEST';
export const QUOTE_RETURN_LABEL_SUCCESS = 'app/TransactionPage/QUOTE_RETURN_LABEL_SUCCESS';
export const QUOTE_RETURN_LABEL_ERROR = 'app/TransactionPage/QUOTE_RETURN_LABEL_ERROR';
export const CLEAR_RETURN_LABEL_QUOTE = 'app/TransactionPage/CLEAR_RETURN_LABEL_QUOTE';
export const RETURN_LABEL_BOUGHT = 'app/TransactionPage/RETURN_LABEL_BOUGHT';

export const ORDER_PHOTOS_UPLOAD_REQUEST = 'app/TransactionPage/ORDER_PHOTOS_UPLOAD_REQUEST';
export const ORDER_PHOTO_UPLOAD_DONE = 'app/TransactionPage/ORDER_PHOTO_UPLOAD_DONE';
export const ORDER_PHOTOS_CLEAR = 'app/TransactionPage/ORDER_PHOTOS_CLEAR';

export const FETCH_RETURN_REFUND_REQUEST = 'app/TransactionPage/FETCH_RETURN_REFUND_REQUEST';
export const FETCH_RETURN_REFUND_SUCCESS = 'app/TransactionPage/FETCH_RETURN_REFUND_SUCCESS';
export const FETCH_RETURN_REFUND_ERROR = 'app/TransactionPage/FETCH_RETURN_REFUND_ERROR';

export const MARK_RETURN_SHIPPED_REQUEST = 'app/TransactionPage/MARK_RETURN_SHIPPED_REQUEST';
export const MARK_RETURN_SHIPPED_SUCCESS = 'app/TransactionPage/MARK_RETURN_SHIPPED_SUCCESS';
export const MARK_RETURN_SHIPPED_ERROR = 'app/TransactionPage/MARK_RETURN_SHIPPED_ERROR';

export const CUSTOMER_REQUEST_RETURN_REQUEST =
  'app/TransactionPage/CUSTOMER_REQUEST_RETURN_REQUEST';
export const CUSTOMER_REQUEST_RETURN_SUCCESS =
  'app/TransactionPage/CUSTOMER_REQUEST_RETURN_SUCCESS';
export const CUSTOMER_REQUEST_RETURN_ERROR = 'app/TransactionPage/CUSTOMER_REQUEST_RETURN_ERROR';

export const CLEAR_RETURN_STATE = 'app/TransactionPage/CLEAR_RETURN_STATE';

export const LABEL_PAYMENT_INTENT_REQUEST = 'app/TransactionPage/LABEL_PAYMENT_INTENT_REQUEST';
export const LABEL_PAYMENT_INTENT_SUCCESS = 'app/TransactionPage/LABEL_PAYMENT_INTENT_SUCCESS';
export const LABEL_PAYMENT_INTENT_ERROR = 'app/TransactionPage/LABEL_PAYMENT_INTENT_ERROR';
export const CLEAR_LABEL_PAYMENT_INTENT = 'app/TransactionPage/CLEAR_LABEL_PAYMENT_INTENT';

// ================ Reducer ================ //

// The label window checks two addresses, the seller's and where returns go:
// the same check, each kept under its own names.
const ADDRESS_CHECK_KEYS = {
  sender: {
    inProgress: 'validateAddressInProgress',
    result: 'addressValidationResult',
    errors: 'addressValidationErrors',
    hasCorrections: 'hasCorrections',
    corrected: 'correctedAddress',
  },
  return: {
    inProgress: 'validateReturnAddressInProgress',
    result: 'returnAddressValidationResult',
    errors: 'returnAddressValidationErrors',
    hasCorrections: 'returnHasCorrections',
    corrected: 'returnCorrectedAddress',
  },
};
const addressCheck = (which, { inProgress = false, result = null, errors = [], corrected }) => {
  const keys = ADDRESS_CHECK_KEYS[which];
  return {
    [keys.inProgress]: inProgress,
    [keys.result]: result,
    [keys.errors]: errors,
    [keys.hasCorrections]: !!corrected,
    [keys.corrected]: corrected || null,
  };
};

const initialState = {
  // Photos being uploaded, by the field that picked them (a window's photo field):
  // { [fieldId]: { pending: [{ key, preview }], error } }. A photo leaves `pending` as its
  // upload ends; the field gets the links when the whole batch has.
  orderPhotoUploads: {},
  // The order the page shows: answers that come back for another (a slow
  // one from the order viewed before) are dropped.
  orderId: null,
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  transitionInProgress: null,
  transitionError: null,
  // The transition that failed: its button (or window) says so, no other.
  transitionErrorFor: null,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  messages: [],
  initialMessageFailedToTransaction: null,
  // Checkout couldn't save the card the buyer asked it to keep (the order page says so)
  savePaymentMethodFailed: false,
  sendMessageInProgress: false,
  sendMessageError: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  ...addressCheck('sender', {}),
  ...addressCheck('return', {}),
  processTransitions: null,
  submitManualTrackingInProgress: false,
  submitManualTrackingError: null,
  // The card payment prepared for a shipping label (the label window's payment step): what
  // it is for (`key`, the seller's choice) and its client secret once made. The latest ask
  // wins: an older answer, or one arriving after the step opened afresh, is dropped. A
  // failure keeps the choice it was for (error.key).
  labelPaymentIntent: null,
  labelPaymentIntentInProgress: false,
  labelPaymentIntentError: null,
  labelPaymentIntentRequest: 0,
  // Return state
  calculateReturnInProgress: false,
  calculateReturnError: null,
  refundPreview: null,
  // The latest preview asked for: an older answer arriving after it is dropped.
  refundPreviewRequest: 0,
  // A return label's price and package (the return label window), and the latest ask
  // for it: an older answer arriving after it, or after the window closed, is dropped.
  returnLabelQuote: null,
  returnLabelQuoteInProgress: false,
  returnLabelQuoteError: null,
  returnLabelQuoteRequest: 0,
  // The return label just bought in that window ({ labelUrl, labelUrlPdf, ... }): its
  // link shows there until the window closes
  returnLabelBought: null,
  // What the open return refunds the buyer (the seller's confirm window says it), and
  // the latest ask for it: an older answer arriving after it is dropped.
  returnRefund: null,
  returnRefundInProgress: false,
  returnRefundError: null,
  returnRefundRequest: 0,
  // Manual return actions
  markReturnShippedInProgress: false,
  markReturnShippedError: null,
  // Customer request return (buyer-initiated)
  customerRequestReturnInProgress: false,
  customerRequestReturnError: null,
};

// Merge entity arrays using ids, so that conflicting items in newer array (b) overwrite old values (a).
// const a = [{ id: { uuid: 1 } }, { id: { uuid: 3 } }];
// const b = [{ id: : { uuid: 2 } }, { id: : { uuid: 1 } }];
// mergeEntityArrays(a, b)
// => [{ id: { uuid: 3 } }, { id: : { uuid: 2 } }, { id: : { uuid: 1 } }]
const mergeEntityArrays = (a, b) => {
  return a.filter(aEntity => !b.find(bEntity => aEntity.id.uuid === bEntity.id.uuid)).concat(b);
};

const uuidOf = id => (id && typeof id === 'object' ? id.uuid : id) || null;

// An answer about an order other than the one shown (tagged with meta.orderId).
const isForOtherOrder = (state, action) => {
  const orderId = action.meta?.orderId;
  return !!state.orderId && !!orderId && orderId !== state.orderId;
};
const ORDER_TAGGED = [
  'FETCH_TRANSACTION_REQUEST',
  'FETCH_TRANSACTION_SUCCESS',
  'FETCH_TRANSACTION_ERROR',
  'FETCH_TRANSITIONS_SUCCESS',
  'FETCH_MESSAGES_REQUEST',
  'FETCH_MESSAGES_SUCCESS',
  'FETCH_MESSAGES_ERROR',
].map(name => `app/TransactionPage/${name}`);

export default function transactionPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  if (ORDER_TAGGED.includes(type) && isForOtherOrder(state, action)) {
    return state;
  }
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case FETCH_TRANSACTION_REQUEST:
      return { ...state, fetchTransactionInProgress: true, fetchTransactionError: null };
    case FETCH_TRANSACTION_SUCCESS: {
      const transactionRef = { id: payload.data.data.id, type: 'transaction' };
      return { ...state, fetchTransactionInProgress: false, transactionRef };
    }
    case FETCH_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransactionInProgress: false, fetchTransactionError: payload };

    case FETCH_TRANSITIONS_SUCCESS:
      return { ...state, processTransitions: payload };

    case TRANSITION_REQUEST:
      return {
        ...state,
        transitionInProgress: payload,
        transitionError: null,
        transitionErrorFor: null,
      };
    case TRANSITION_SUCCESS:
      return {
        ...state,
        transitionInProgress: null,
      };
    case TRANSITION_ERROR:
      // A step of an order no longer shown only lets go of the buttons.
      return isForOtherOrder(state, action)
        ? { ...state, transitionInProgress: null }
        : {
            ...state,
            transitionInProgress: null,
            transitionError: payload,
            transitionErrorFor: action.meta?.transition || null,
          };

    case FETCH_MESSAGES_REQUEST:
      return { ...state, fetchMessagesInProgress: true, fetchMessagesError: null };
    case FETCH_MESSAGES_SUCCESS: {
      const oldestMessagePageFetched =
        state.oldestMessagePageFetched > payload.page
          ? state.oldestMessagePageFetched
          : payload.page;
      return {
        ...state,
        fetchMessagesInProgress: false,
        messages: mergeEntityArrays(state.messages, payload.messages),
        totalMessages: payload.totalItems,
        totalMessagePages: payload.totalPages,
        oldestMessagePageFetched,
      };
    }
    case FETCH_MESSAGES_ERROR:
      return { ...state, fetchMessagesInProgress: false, fetchMessagesError: payload };

    case SEND_MESSAGE_REQUEST:
      return {
        ...state,
        sendMessageInProgress: true,
        sendMessageError: null,
        initialMessageFailedToTransaction: null,
      };
    case SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false, sendMessageError: payload };

    case SEND_REVIEW_REQUEST:
      return { ...state, sendReviewInProgress: true, sendReviewError: null };
    case SEND_REVIEW_SUCCESS:
      return { ...state, sendReviewInProgress: false };
    case SEND_REVIEW_ERROR:
      return { ...state, sendReviewInProgress: false, sendReviewError: payload };
    case CLEAR_SEND_REVIEW_ERROR:
      return { ...state, sendReviewError: null };

    case VALIDATE_ADDRESS_REQUEST:
      return { ...state, ...addressCheck(payload.which, { inProgress: true }) };
    case VALIDATE_ADDRESS_SUCCESS:
      return {
        ...state,
        ...addressCheck(payload.which, {
          result: payload.isValid ? 'success' : 'error',
          errors: payload.errors,
          corrected: payload.isValid && payload.hasCorrections ? payload.correctedAddress : null,
        }),
      };
    case VALIDATE_ADDRESS_ERROR:
      return { ...state, ...addressCheck(payload.which, { result: 'error' }) };
    case CLEAR_ADDRESS_VALIDATION:
      return { ...state, ...addressCheck(payload.which, {}) };

    case SUBMIT_MANUAL_TRACKING_REQUEST:
      return { ...state, submitManualTrackingInProgress: true, submitManualTrackingError: null };
    case SUBMIT_MANUAL_TRACKING_SUCCESS:
      return { ...state, submitManualTrackingInProgress: false };
    case SUBMIT_MANUAL_TRACKING_ERROR:
      return {
        ...state,
        submitManualTrackingInProgress: false,
        submitManualTrackingError: payload,
      };

    case LABEL_PAYMENT_INTENT_REQUEST:
      return {
        ...state,
        labelPaymentIntent: { key: payload.key, clientSecret: null },
        labelPaymentIntentInProgress: true,
        labelPaymentIntentError: null,
        labelPaymentIntentRequest: payload.request,
      };
    case LABEL_PAYMENT_INTENT_SUCCESS:
      return action.meta?.request !== state.labelPaymentIntentRequest
        ? state
        : {
            ...state,
            labelPaymentIntent: { key: action.meta.key, clientSecret: payload },
            labelPaymentIntentInProgress: false,
          };
    case LABEL_PAYMENT_INTENT_ERROR:
      return action.meta?.request !== state.labelPaymentIntentRequest
        ? state
        : {
            ...state,
            labelPaymentIntent: null,
            labelPaymentIntentInProgress: false,
            labelPaymentIntentError: { ...payload, key: action.meta.key },
          };
    case CLEAR_LABEL_PAYMENT_INTENT:
      return {
        ...state,
        labelPaymentIntent: null,
        labelPaymentIntentInProgress: false,
        labelPaymentIntentError: null,
        labelPaymentIntentRequest: 0,
      };

    case CALCULATE_RETURN_REQUEST:
      return {
        ...state,
        calculateReturnInProgress: true,
        calculateReturnError: null,
        refundPreview: null,
        refundPreviewRequest: payload,
      };
    case CALCULATE_RETURN_SUCCESS:
      return action.meta?.request !== state.refundPreviewRequest
        ? state
        : {
            ...state,
            calculateReturnInProgress: false,
            refundPreview: payload,
          };
    case CALCULATE_RETURN_ERROR:
      return action.meta?.request !== state.refundPreviewRequest
        ? state
        : {
            ...state,
            calculateReturnInProgress: false,
            calculateReturnError: payload,
          };

    case QUOTE_RETURN_LABEL_REQUEST:
      return {
        ...state,
        returnLabelQuoteInProgress: true,
        returnLabelQuoteError: null,
        returnLabelQuoteRequest: payload,
      };
    case QUOTE_RETURN_LABEL_SUCCESS:
      return action.meta?.request !== state.returnLabelQuoteRequest
        ? state
        : { ...state, returnLabelQuoteInProgress: false, returnLabelQuote: payload };
    case QUOTE_RETURN_LABEL_ERROR:
      return action.meta?.request !== state.returnLabelQuoteRequest
        ? state
        : { ...state, returnLabelQuoteInProgress: false, returnLabelQuoteError: payload };
    case CLEAR_RETURN_LABEL_QUOTE:
      return {
        ...state,
        returnLabelQuote: null,
        returnLabelQuoteInProgress: false,
        returnLabelQuoteError: null,
        returnLabelQuoteRequest: 0,
        returnLabelBought: null,
      };
    case RETURN_LABEL_BOUGHT:
      return { ...state, returnLabelBought: payload };

    case ORDER_PHOTOS_UPLOAD_REQUEST:
      return {
        ...state,
        orderPhotoUploads: {
          ...state.orderPhotoUploads,
          [payload.fieldId]: { pending: payload.pending, error: null },
        },
      };
    case ORDER_PHOTO_UPLOAD_DONE: {
      const field = state.orderPhotoUploads[payload.fieldId];
      if (!field) return state;
      return {
        ...state,
        orderPhotoUploads: {
          ...state.orderPhotoUploads,
          [payload.fieldId]: {
            pending: field.pending.filter(p => p.key !== payload.key),
            error: payload.error || field.error,
          },
        },
      };
    }
    case ORDER_PHOTOS_CLEAR: {
      const { [payload]: cleared, ...others } = state.orderPhotoUploads;
      return { ...state, orderPhotoUploads: others };
    }

    case FETCH_RETURN_REFUND_REQUEST:
      return {
        ...state,
        returnRefund: null,
        returnRefundInProgress: true,
        returnRefundError: null,
        returnRefundRequest: payload,
      };
    case FETCH_RETURN_REFUND_SUCCESS:
      return action.meta?.request !== state.returnRefundRequest
        ? state
        : { ...state, returnRefundInProgress: false, returnRefund: payload };
    case FETCH_RETURN_REFUND_ERROR:
      return action.meta?.request !== state.returnRefundRequest
        ? state
        : { ...state, returnRefundInProgress: false, returnRefundError: payload };

    case CLEAR_RETURN_STATE:
      return {
        ...state,
        calculateReturnInProgress: false,
        calculateReturnError: null,
        refundPreview: null,
        customerRequestReturnError: null,
      };

    // Manual return shipped
    case MARK_RETURN_SHIPPED_REQUEST:
      return { ...state, markReturnShippedInProgress: true, markReturnShippedError: null };
    case MARK_RETURN_SHIPPED_SUCCESS:
      return { ...state, markReturnShippedInProgress: false };
    case MARK_RETURN_SHIPPED_ERROR:
      return { ...state, markReturnShippedInProgress: false, markReturnShippedError: payload };

    // Customer request return (buyer-initiated)
    case CUSTOMER_REQUEST_RETURN_REQUEST:
      return { ...state, customerRequestReturnInProgress: true, customerRequestReturnError: null };
    case CUSTOMER_REQUEST_RETURN_SUCCESS:
      return { ...state, customerRequestReturnInProgress: false };
    case CUSTOMER_REQUEST_RETURN_ERROR:
      return {
        ...state,
        customerRequestReturnInProgress: false,
        customerRequestReturnError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const transitionInProgress = state => {
  return state.TransactionPage.transitionInProgress;
};

// ================ Action creators ================ //
export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

// Actions about one order carry its id (meta.orderId), so the reducer can
// drop those about an order no longer shown.
const forOrder = id => ({ meta: { orderId: uuidOf(id) } });

const fetchTransactionRequest = id => ({ type: FETCH_TRANSACTION_REQUEST, ...forOrder(id) });
const fetchTransactionSuccess = (id, response) => ({
  type: FETCH_TRANSACTION_SUCCESS,
  payload: response,
  ...forOrder(id),
});
const fetchTransactionError = (id, e) => ({
  type: FETCH_TRANSACTION_ERROR,
  error: true,
  payload: e,
  ...forOrder(id),
});

const fetchTransitionsSuccess = (id, response) => ({
  type: FETCH_TRANSITIONS_SUCCESS,
  payload: response,
  ...forOrder(id),
});

const transitionRequest = transitionName => ({ type: TRANSITION_REQUEST, payload: transitionName });
const transitionSuccess = () => ({ type: TRANSITION_SUCCESS });
const transitionError = (id, transitionName, e) => ({
  type: TRANSITION_ERROR,
  error: true,
  payload: e,
  meta: { orderId: uuidOf(id), transition: transitionName },
});

const fetchMessagesRequest = id => ({ type: FETCH_MESSAGES_REQUEST, ...forOrder(id) });
const fetchMessagesSuccess = (id, messages, pagination) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { messages, ...pagination },
  ...forOrder(id),
});
const fetchMessagesError = (id, e) => ({
  type: FETCH_MESSAGES_ERROR,
  error: true,
  payload: e,
  ...forOrder(id),
});

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });
const sendMessageSuccess = () => ({ type: SEND_MESSAGE_SUCCESS });
const sendMessageError = e => ({ type: SEND_MESSAGE_ERROR, error: true, payload: e });

const sendReviewRequest = () => ({ type: SEND_REVIEW_REQUEST });
const sendReviewSuccess = () => ({ type: SEND_REVIEW_SUCCESS });
const sendReviewError = e => ({ type: SEND_REVIEW_ERROR, error: true, payload: e });
/** The review window opens and closes without an earlier attempt's error. */
export const clearSendReviewError = () => ({ type: CLEAR_SEND_REVIEW_ERROR });

export const clearAddressValidation = () => ({
  type: CLEAR_ADDRESS_VALIDATION,
  payload: { which: 'sender' },
});
export const clearReturnAddressValidation = () => ({
  type: CLEAR_ADDRESS_VALIDATION,
  payload: { which: 'return' },
});

const submitManualTrackingRequest = () => ({ type: SUBMIT_MANUAL_TRACKING_REQUEST });
const submitManualTrackingSuccess = () => ({ type: SUBMIT_MANUAL_TRACKING_SUCCESS });
const submitManualTrackingError = e => ({
  type: SUBMIT_MANUAL_TRACKING_ERROR,
  error: true,
  payload: e,
});

const calculateReturnRequest = request => ({ type: CALCULATE_RETURN_REQUEST, payload: request });
const calculateReturnSuccess = (request, payload) => ({
  type: CALCULATE_RETURN_SUCCESS,
  payload,
  meta: { request },
});
const calculateReturnError = (request, e) => ({
  type: CALCULATE_RETURN_ERROR,
  error: true,
  payload: e,
  meta: { request },
});

const quoteReturnLabelRequest = request => ({ type: QUOTE_RETURN_LABEL_REQUEST, payload: request });
const quoteReturnLabelSuccess = (request, payload) => ({
  type: QUOTE_RETURN_LABEL_SUCCESS,
  payload,
  meta: { request },
});
const quoteReturnLabelError = (request, e) => ({
  type: QUOTE_RETURN_LABEL_ERROR,
  error: true,
  payload: e,
  meta: { request },
});
/**
 * The return label window closed (or opens afresh): its quote, any answer on its way, and
 * a label bought in it, go.
 */
export const clearReturnLabelQuote = () => ({ type: CLEAR_RETURN_LABEL_QUOTE });

const fetchReturnRefundRequest = request => ({
  type: FETCH_RETURN_REFUND_REQUEST,
  payload: request,
});
const fetchReturnRefundSuccess = (request, payload) => ({
  type: FETCH_RETURN_REFUND_SUCCESS,
  payload,
  meta: { request },
});
const fetchReturnRefundError = (request, e) => ({
  type: FETCH_RETURN_REFUND_ERROR,
  error: true,
  payload: e,
  meta: { request },
});

const markReturnShippedRequest = () => ({ type: MARK_RETURN_SHIPPED_REQUEST });
const markReturnShippedSuccess = payload => ({ type: MARK_RETURN_SHIPPED_SUCCESS, payload });
const markReturnShippedError = e => ({ type: MARK_RETURN_SHIPPED_ERROR, error: true, payload: e });

const customerRequestReturnRequest = () => ({ type: CUSTOMER_REQUEST_RETURN_REQUEST });
const customerRequestReturnSuccess = payload => ({
  type: CUSTOMER_REQUEST_RETURN_SUCCESS,
  payload,
});
const customerRequestReturnError = e => ({
  type: CUSTOMER_REQUEST_RETURN_ERROR,
  error: true,
  payload: e,
});

export const clearReturnState = () => ({ type: CLEAR_RETURN_STATE });

// ================ Thunks ================ //

// Helper to fetch correct image variants for different thunk calls
const getImageVariants = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

const listingRelationship = txResponse => {
  return txResponse.data.data.relationships.listing.data;
};

/**
 * The order, its listing and the people on it.
 *
 * @param {UUID} id
 * @param {Object} config
 * @param {Object} [options]
 * @param {boolean} [options.quiet] a background refresh of an order already
 *   shown: a failure is logged and the page keeps what it shows (only the
 *   page's own load says the order couldn't be read)
 */
export const fetchTransaction = (id, config, { quiet = false } = {}) => (
  dispatch,
  getState,
  sdk
) => {
  if (!quiet) dispatch(fetchTransactionRequest(id));
  let txResponse = null;

  return sdk.transactions
    .show(
      {
        id,
        include: [
          'customer',
          'customer.profileImage',
          'provider',
          'provider.profileImage',
          'listing',
          'listing.currentStock',
          'booking',
          'reviews',
          'reviews.author',
          'reviews.subject',
        ],
        'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
        ...getImageVariants(config.layout.listingImage),
      },
      { expand: true }
    )
    .then(response => {
      txResponse = response;
      const listingId = listingRelationship(response).id;
      const listing = (response.data.included || []).find(
        entity => entity.type === 'listing' && entity.id.uuid === listingId.uuid
      );
      const canFetchListing = listing && listing.attributes && !listing.attributes.deleted;
      if (canFetchListing) {
        return sdk.listings.show({
          id: listingId,
          include: ['author', 'author.profileImage', 'images'],
          ...getImageVariants(config.layout.listingImage),
        });
      } else {
        return response;
      }
    })
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(txResponse, sanitizeConfig));
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(fetchTransactionSuccess(id, txResponse));
      return response;
    })
    .catch(e => {
      if (quiet) {
        log.error(e, 'order-refresh-failed', { txId: uuidOf(id) });
        return null;
      }
      dispatch(fetchTransactionError(id, storableError(e)));
      throw e;
    });
};

export const fetchNextTransitions = id => (dispatch, getState, sdk) => {
  return sdk.processTransitions
    .query({ transactionId: id })
    .then(res => {
      dispatch(fetchTransitionsSuccess(id, res.data.data));
    })
    .catch(e => {
      // The buttons stay as they were; the next read of the order tries again.
      log.error(e, 'fetch-next-transitions-failed', { txId: uuidOf(id) });
    });
};

const refreshTx = (sdk, txId) => sdk.transactions.show({ id: txId }, { expand: true });

const stateOf = tx => {
  try {
    const processName = resolveLatestProcessName(tx?.attributes?.processName);
    return getProcess(processName).getState(tx);
  } catch (e) {
    return null;
  }
};

/**
 * After a step the order and what can be done next are read again a little
 * later: the process may have moved the order on by itself (received →
 * completed, which brings the review and return buttons). While it still
 * sits in a state the process leaves at once, it is read again once more.
 */
export const REFRESH_AFTER_STEP_MS = [3000, 8000];
const refreshAfterStep = (sdk, txId, dispatch) => {
  const refresh = attempt =>
    delay(REFRESH_AFTER_STEP_MS[attempt])
      .then(() => refreshTx(sdk, txId))
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        dispatch(fetchNextTransitions(txId));
        const passing = PASSING_STATES.includes(stateOf(response?.data?.data));
        return passing && attempt + 1 < REFRESH_AFTER_STEP_MS.length ? refresh(attempt + 1) : null;
      });
  return refresh(0).catch(e => {
    // A background refresh: the order stays as it was shown.
    log.error(e, 'order-refresh-after-step-failed', { txId: uuidOf(txId) });
  });
};

export const makeTransition = (txId, transitionName, params) => (dispatch, getState, sdk) => {
  if (transitionInProgress(getState())) {
    return Promise.reject(new Error('Transition already in progress'));
  }
  dispatch(transitionRequest(transitionName));

  // A step only our server can fire (a cancellation, a dispute, a return...)
  // goes through it: it checks the step, builds any refund and records the
  // details in the same transition. Anything else goes straight to Sharetribe.
  const privileged = isPrivilegedPurchaseTransition(transitionName);
  const bodyParams = { id: txId, transition: transitionName, params };
  const sent = privileged
    ? transitionPrivileged({ bodyParams, queryParams: { expand: true } })
    : sdk.transactions.transition(bodyParams, { expand: true });

  return sent
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(transitionSuccess());
      dispatch(fetchCurrentUserNotifications());

      // Our server records part of a step just after its transition (a
      // label's status, a return's label), which the answer doesn't carry:
      // the order is read again at once.
      if (privileged) {
        refreshTx(sdk, txId)
          .then(fresh => dispatch(addMarketplaceEntities(fresh)))
          .catch(e => log.error(e, 'order-refresh-failed', { txId: uuidOf(txId) }));
      }
      dispatch(fetchNextTransitions(txId));
      refreshAfterStep(sdk, txId, dispatch);

      return response;
    })
    .catch(e => {
      dispatch(transitionError(txId, transitionName, storableError(e)));
      log.error(e, `${transitionName}-failed`, {
        txId,
        transition: transitionName,
      });
      throw e;
    });
};

/**
 * The seller puts what a refunded return brought back in stock again. Shown
 * like a step (the same in-progress and error), but it moves the order
 * nowhere: the server records it in the order's metadata, read again here.
 *
 * Resolves with the server's answer: { already, listings: [{ listingId, added,
 * opened } | { listingId, error }] }.
 */
export const relistReturnedItems = txId => (dispatch, getState, sdk) => {
  if (transitionInProgress(getState())) {
    return Promise.reject(new Error('Transition already in progress'));
  }
  dispatch(transitionRequest(RELIST));
  return relistOrderItems({ transactionId: uuidOf(txId) })
    .then(result =>
      refreshTx(sdk, txId).then(response => {
        dispatch(addMarketplaceEntities(response));
        dispatch(transitionSuccess());
        return result || {};
      })
    )
    .catch(e => {
      dispatch(transitionError(txId, RELIST, storableError(e)));
      log.error(e, 'relist-failed', { txId });
      throw e;
    });
};

const fetchMessages = (txId, page, config) => (dispatch, getState, sdk) => {
  const paging = { page, perPage: MESSAGES_PAGE_SIZE };
  dispatch(fetchMessagesRequest(txId));

  return sdk.messages
    .query({
      transaction_id: txId,
      include: ['sender', 'sender.profileImage'],
      ...getImageVariants(config.layout.listingImage),
      ...paging,
    })
    .then(response => {
      const messages = denormalisedResponseEntities(response);
      const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
      const pagination = { totalItems, totalPages, page: fetchedPage };
      const totalMessages = getState().TransactionPage.totalMessages;

      // Original fetchMessages call succeeded
      dispatch(fetchMessagesSuccess(txId, messages, pagination));

      // Check if totalItems has changed between fetched pagination pages
      // if totalItems has changed, fetch first page again to include new incoming messages.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      if (totalItems > totalMessages && page > 1) {
        dispatch(fetchMessages(txId, 1, config))
          .then(() => {
            // Original fetch was enough as a response for user action,
            // this just includes new incoming messages
          })
          .catch(() => {
            // Background update, no need to to do anything atm.
          });
      }
    })
    .catch(e => {
      dispatch(fetchMessagesError(txId, storableError(e)));
      throw e;
    });
};

export const fetchMoreMessages = (txId, config) => (dispatch, getState, sdk) => {
  const state = getState();
  const { oldestMessagePageFetched, totalMessagePages } = state.TransactionPage;
  const hasMoreOldMessages = totalMessagePages > oldestMessagePageFetched;

  // In case there're no more old pages left we default to fetching the current cursor position
  const nextPage = hasMoreOldMessages ? oldestMessagePageFetched + 1 : oldestMessagePageFetched;

  return dispatch(fetchMessages(txId, nextPage, config));
};

export const sendMessage = (txId, message, config) => (dispatch, getState, sdk) => {
  dispatch(sendMessageRequest());

  return sdk.messages
    .send({ transactionId: txId, content: message })
    .then(response => {
      const messageId = response.data.data.id;

      // We fetch the first page again to add sent message to the page data
      // and update possible incoming messages too.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      return dispatch(fetchMessages(txId, 1, config))
        .then(() => {
          dispatch(sendMessageSuccess());

          // Track notification for the other party
          const state = getState();
          const currentUser = state.user.currentUser;
          const txRef = { id: txId, type: 'transaction' };
          const transactions = getMarketplaceEntities(state, [txRef]);
          const transaction = transactions.length > 0 ? transactions[0] : null;

          if (transaction && currentUser) {
            // Track notification asynchronously - don't block on this
            trackNotificationAuto({
              transaction,
              currentUserId: currentUser.id,
            }).catch(err => {
              log.error(err, 'message-notification-tracking-failed', {
                txId: txId.uuid || txId,
              });
            });
          }

          return messageId;
        })
        .catch(() => {
          // Sent: only reading the messages again failed (the next read shows it)
          dispatch(sendMessageSuccess());
          return messageId;
        });
    })
    .catch(e => {
      dispatch(sendMessageError(storableError(e)));
      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

// If other party has already sent a review, we need to make transition to
// transitions.REVIEW_2_BY_<CUSTOMER/PROVIDER>
const sendReviewAsSecond = (txId, transition, params, dispatch, sdk, config) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      // A review moves the order on: what the buyer can do next (e.g. a return) changes
      dispatch(fetchNextTransitions(txId));
      return response;
    })
    .catch(e => {
      dispatch(sendReviewError(storableError(e)));

      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

// If other party has not yet sent a review, we need to make transition to
// transitions.REVIEW_1_BY_<CUSTOMER/PROVIDER>
// However, the other party might have made the review after previous data synch point.
// So, error is likely to happen and then we must try another state transition
// by calling sendReviewAsSecond().
const sendReviewAsFirst = (txId, transition, params, dispatch, sdk, config) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      // A review moves the order on: what the buyer can do next (e.g. a return) changes
      dispatch(fetchNextTransitions(txId));
      return response;
    })
    .catch(e => {
      // If transaction transition is invalid, lets try another endpoint.
      if (isTransactionsTransitionInvalidTransition(e)) {
        return sendReviewAsSecond(txId, transition, params, dispatch, sdk, config);
      } else {
        dispatch(sendReviewError(storableError(e)));

        // Rethrow so the page can track whether the sending failed, and
        // keep the message in the form for a retry.
        throw e;
      }
    });
};

export const sendReview = (tx, transitionOptionsInfo, params, config) => (
  dispatch,
  getState,
  sdk
) => {
  const { reviewAsFirst, reviewAsSecond, hasOtherPartyReviewedFirst } = transitionOptionsInfo;
  dispatch(sendReviewRequest());

  return hasOtherPartyReviewedFirst
    ? sendReviewAsSecond(tx?.id, reviewAsSecond, params, dispatch, sdk, config)
    : sendReviewAsFirst(tx?.id, reviewAsFirst, params, dispatch, sdk, config);
};

const isNonEmpty = value => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

// ================ Create Payment Intent for Shipping Label ================ //

/**
 * A card payment for a shipping label (the label window's payment step), for the seller's
 * choice `key`: resolves with the server's answer ({ clientSecret }, or for a saved card
 * paid at once { succeeded, requiresAction, clientSecret, paymentIntentId }); a refusal
 * rejects. The answer for the latest ask is kept in the state; the step confirms it.
 */
let labelPaymentIntentRequests = 0;
export const requestCreatePaymentIntent = (paymentData, key = null) => dispatch => {
  labelPaymentIntentRequests += 1;
  const request = labelPaymentIntentRequests;
  const meta = { request, key };
  dispatch({ type: LABEL_PAYMENT_INTENT_REQUEST, payload: { request, key } });
  return createLabelPaymentIntent(paymentData)
    .then(response => {
      dispatch({
        type: LABEL_PAYMENT_INTENT_SUCCESS,
        payload: response?.clientSecret || null,
        meta,
      });
      return response;
    })
    .catch(e => {
      // util/api puts the server's reason on the error: a card refusal's in `message`,
      // another refusal's in `error`
      const reason = e?.message || e?.error || null;
      dispatch({
        type: LABEL_PAYMENT_INTENT_ERROR,
        error: true,
        payload: { ...storableError(e), reason },
        meta,
      });
      throw e;
    });
};

/** The payment step opens afresh: a payment prepared before, and any on its way, go. */
export const clearLabelPaymentIntent = () => ({ type: CLEAR_LABEL_PAYMENT_INTENT });

// ================ Submit Manual Tracking ================ //

export const submitManualTracking = (transactionId, trackingData, config) => dispatch => {
  dispatch(submitManualTrackingRequest());

  const requestBody = {
    transactionId,
    ...trackingData,
  };

  return submitManualTrackingApi(requestBody).then(
    response => {
      dispatch(submitManualTrackingSuccess());
      dispatch(fetchCurrentUserNotifications());
      // The order is shipped: read again in the background (a failure to
      // read it doesn't undo the shipment, nor fail the window).
      dispatch(fetchTransaction(new UUID(transactionId), config, { quiet: true }));
      return response;
    },
    error => {
      dispatch(submitManualTrackingError(storableError(error)));
      throw error;
    }
  );
};

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
/**
 * The viewer has seen the order: its unread count for them goes back to 0, and the
 * topbar's count is read again. In the browser only (the page's server render has no
 * session for it); a failure only leaves the count as it was.
 */
export const markOrderSeen = txId => (dispatch, getState) => {
  const { currentUser } = getState().user;
  if (typeof window === 'undefined' || !currentUser) return Promise.resolve();
  let transaction = null;
  try {
    [transaction] = getMarketplaceEntities(getState(), [{ id: txId, type: 'transaction' }]);
  } catch (e) {
    // Not in the store as a whole order: the page's own load says why
  }
  if (!transaction?.customer) return Promise.resolve();
  return resetNotificationAuto({ transaction, currentUserId: currentUser.id })
    .then(() => dispatch(fetchCurrentUserNotifications()))
    .catch(e => log.error(e, 'mark-order-seen-failed', { txId: uuidOf(txId) }));
};

export const loadData = (params, search, config) => (dispatch, getState) => {
  const txId = new UUID(params.id);
  const state = getState().TransactionPage;
  const txRef = state.transactionRef;
  // In case a transaction reference is found from a previous
  // data load -> clear the state. Otherwise keep the non-null
  // and non-empty values which may have been set from a previous page.
  const initialValues = txRef ? {} : pickBy(state, isNonEmpty);
  dispatch(setInitialValues({ ...initialValues, orderId: params.id }));
  // A label bought on another order's page isn't this order's.
  dispatch(clearLabelBoughtForOtherOrder(params.id));

  // Sale / order (i.e. transaction entity in API)
  return Promise.all([
    dispatch(fetchTransaction(txId, config)),
    dispatch(fetchMessages(txId, 1, config)),
    dispatch(fetchNextTransitions(txId)),
    dispatch(fetchCurrentUser()),
  ]).then(loaded => {
    dispatch(markOrderSeen(txId));
    return loaded;
  });
};

// ================ Address Validation ================ //

/**
 * Checks an address of the label window with the carrier (the seller's, or
 * where returns go: `which`). Resolves with { isValid, hasCorrections,
 * correctedAddress }; the reasons it isn't valid are in the state.
 */
const validateAddress = which => addressData => dispatch => {
  dispatch({ type: VALIDATE_ADDRESS_REQUEST, payload: { which } });
  return checkAddressWithCarrier(addressData)
    .then(({ isValid, hasCorrections, correctedAddress, errors }) => {
      const result = { isValid, hasCorrections, correctedAddress };
      dispatch({ type: VALIDATE_ADDRESS_SUCCESS, payload: { which, ...result, errors } });
      return result;
    })
    .catch(error => {
      log.error(error, 'address-validation-failed', { which });
      dispatch({ type: VALIDATE_ADDRESS_ERROR, error: true, payload: { which } });
      return { isValid: false, hasCorrections: false, correctedAddress: null };
    });
};
export const requestValidateAddress = validateAddress('sender');
export const requestValidateReturnAddress = validateAddress('return');

// ================ Return Management ================ //

/** What a return would refund the buyer (the server works it out as it will refund it). */
let refundPreviewRequests = 0;
export const calculateReturn = params => dispatch => {
  refundPreviewRequests += 1;
  const request = refundPreviewRequests;
  dispatch(calculateReturnRequest(request));
  const { transactionId, returnReason, items } = params;
  return BackgroundServiceApi.returnRefundPreview({
    transactionId: uuidOf(transactionId),
    returnReason,
    ...(Array.isArray(items) && items.length ? { items } : {}),
  })
    .then(response => {
      dispatch(calculateReturnSuccess(request, response));
      return response;
    })
    .catch(error => {
      dispatch(calculateReturnError(request, storableError(error)));
      throw error;
    });
};

/**
 * A return label's price and package, for the buyer or the seller to buy it
 * (customer-buy-return-label / provider-send-return-label, which price it again
 * themselves). parcel: the package as edited in the window; null for the listing's.
 */
let returnLabelQuoteRequests = 0;
export const quoteReturnLabel = (transactionId, parcel) => dispatch => {
  returnLabelQuoteRequests += 1;
  const request = returnLabelQuoteRequests;
  dispatch(quoteReturnLabelRequest(request));
  return BackgroundServiceApi.quoteReturnLabel({ transactionId: uuidOf(transactionId), parcel })
    .then(quote => {
      dispatch(quoteReturnLabelSuccess(request, quote));
    })
    .catch(e => {
      dispatch(quoteReturnLabelError(request, storableError(e)));
    });
};

/**
 * Buys the open return's label (the seller sends one: provider-send-return-label; the buyer
 * buys one: customer-buy-return-label). parcel: the package as edited in the window; null
 * for the quoted one. The step's progress and error are the page's (makeTransition); the
 * label bought is kept for the window to link to.
 */
export const buyReturnLabel = (transactionId, isProvider, parcel) => dispatch =>
  dispatch(
    makeTransition(
      transactionId,
      isProvider ? T.PROVIDER_SEND_RETURN_LABEL : T.CUSTOMER_BUY_RETURN_LABEL,
      parcel ? { parcel } : {}
    )
  ).then(response => {
    const label = response?.result?.returnLabel || {};
    dispatch({ type: RETURN_LABEL_BOUGHT, payload: label });
    return label;
  });

/**
 * Uploads photos for the order (a return's, a dispute's, proof of delivery) to the order's
 * own folder, all at once. The field that picked them (fieldId) finds each one's progress and
 * any failure in orderPhotoUploads. One batch at a time per field: a pick while one runs does
 * nothing (resolves null). Resolves with the links that uploaded, in the order picked.
 *
 * @param {Object} p
 * @param {string} p.fieldId the picking field
 * @param {string} p.transactionId
 * @param {Array<{ key, file, preview }>} p.photos preview: the field's local preview, or null
 */
export const uploadOrderPhotos = ({ fieldId, transactionId, photos }) => (dispatch, getState) => {
  if (getState().TransactionPage.orderPhotoUploads[fieldId]?.pending?.length) {
    return Promise.resolve(null);
  }
  dispatch({
    type: ORDER_PHOTOS_UPLOAD_REQUEST,
    payload: { fieldId, pending: photos.map(({ key, preview }) => ({ key, preview })) },
  });
  return Promise.all(
    photos.map(({ key, file }) =>
      uploadOrderPhotoApi(file, transactionId).then(
        url => {
          dispatch({ type: ORDER_PHOTO_UPLOAD_DONE, payload: { fieldId, key } });
          return url;
        },
        e => {
          log.error(e, 'order-photo-upload-failed', { txId: uuidOf(transactionId) });
          dispatch({
            type: ORDER_PHOTO_UPLOAD_DONE,
            payload: { fieldId, key, error: storableError(e) },
          });
          return null;
        }
      )
    )
  ).then(urls => urls.filter(Boolean));
};

/** A photo field opens (or closes): it starts without uploads or an earlier failure. */
export const clearOrderPhotoUploads = fieldId => ({ type: ORDER_PHOTOS_CLEAR, payload: fieldId });

/**
 * What the open return refunds the buyer, as the refund itself works it out: the
 * seller's confirm-return window says it. A failure leaves it unknown (the window
 * then says it without the sum).
 */
let returnRefundRequests = 0;
export const fetchReturnRefund = transactionId => dispatch => {
  returnRefundRequests += 1;
  const request = returnRefundRequests;
  dispatch(fetchReturnRefundRequest(request));
  return fetchOpenReturnRefund(uuidOf(transactionId))
    .then(figures => {
      dispatch(fetchReturnRefundSuccess(request, figures));
    })
    .catch(e => {
      dispatch(fetchReturnRefundError(request, storableError(e)));
      log.error(e, 'fetch-return-refund-failed', { txId: uuidOf(transactionId) });
    });
};

/**
 * The buyer sends their return back (customer-mark-return-shipped, a step of
 * our server): with the label bought on TradMarket its tracking is the label's,
 * otherwise the number they entered (the server finds its carrier) and the
 * carrier's link when the number's carrier isn't known.
 */
export const markReturnShipped = params => (dispatch, getState) => {
  // Another step on its way: this one waits (makeTransition's own refusal isn't this step's error)
  if (transitionInProgress(getState())) {
    return Promise.reject(new Error('Transition already in progress'));
  }
  dispatch(markReturnShippedRequest());
  const { transactionId, trackingData } = params;
  const tracking = trackingData
    ? {
        trackingNumber: trackingData.trackingNumber,
        ...(trackingData.trackingUrl ? { trackingUrl: trackingData.trackingUrl } : {}),
      }
    : {};
  return dispatch(makeTransition(transactionId, T.CUSTOMER_MARK_RETURN_SHIPPED, tracking))
    .then(response => {
      dispatch(markReturnShippedSuccess(response));
      return response;
    })
    .catch(error => {
      dispatch(markReturnShippedError(storableError(error)));
      throw error;
    });
};

/** Which buyer return transition applies to this transaction's current state. */
const customerReturnTransitionFor = tx => {
  const state = tx ? getProcess(PURCHASE_PROCESS_NAME).getState(tx) : null;
  return CUSTOMER_RETURN_TRANSITION_BY_STATE[state] || T.CUSTOMER_REQUEST_RETURN;
};

/**
 * The buyer opens a return (a step of our server, from whichever state the
 * order is in): the server checks the windows and items, decides who pays the
 * return shipping, and records the return.
 *
 * params: { transactionId, returnReason, returnType, returnDescription?, returnPhotos?, items? }
 */
export const customerRequestReturn = params => (dispatch, getState) => {
  // Another step on its way: this one waits (makeTransition's own refusal isn't this step's error)
  if (transitionInProgress(getState())) {
    return Promise.reject(new Error('Transition already in progress'));
  }
  dispatch(customerRequestReturnRequest());
  const {
    transactionId,
    returnReason,
    returnType,
    returnDescription,
    returnPhotos,
    items,
  } = params;
  const txIdStr = uuidOf(transactionId);
  const txEntity = getMarketplaceEntities(getState(), [
    { id: new UUID(txIdStr), type: 'transaction' },
  ])?.[0];
  const input = {
    returnType,
    returnReason,
    ...(returnDescription ? { returnDescription } : {}),
    ...(Array.isArray(returnPhotos) && returnPhotos.length ? { returnPhotos } : {}),
    ...(Array.isArray(items) && items.length ? { items } : {}),
  };
  return dispatch(makeTransition(new UUID(txIdStr), customerReturnTransitionFor(txEntity), input))
    .then(response => {
      dispatch(customerRequestReturnSuccess(response));
      return response;
    })
    .catch(error => {
      // The step's refusal says why (window closed, final sale...): the window shows it.
      dispatch(customerRequestReturnError(storableError(error)));
      throw error;
    });
};
